/**
 * useHashTab — restores URL hash-based tab deep-linking.
 *
 * When a user navigates to /cohorts/123#mentors (e.g. from a notification
 * link), this hook reads the hash, sets the active tab, then clears the hash
 * from the URL so it doesn't persist in browser history.
 *
 * Usage:
 *   const [activeTabId, setActiveTabId] = useHashTab('overview');
 */
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export function useHashTab(defaultTab: string): [string, (id: string) => void] {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTabId, setActiveTabId] = useState(() => {
    const hash = location.hash.replace('#', '');
    return hash || defaultTab;
  });

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setActiveTabId(hash);
      // Clear the hash so it doesn't persist in browser history.
      // Include location.search to preserve any query params on the URL.
      navigate(location.pathname + location.search, { replace: true });
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  return [activeTabId, setActiveTabId];
}
